import { useState } from 'react';
import { LogOut, Users, ChevronDown, ChevronRight, Eye, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import axios from 'axios';
import { toast } from 'sonner';
import NotificationCenter from '@/components/shared/NotificationCenter';
import { useNavigate } from 'react-router-dom';

// Veecheck Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_veecheck-staging/artifacts/w5naci8s_Veecheck%20logo.png";

// Demo accounts removed for production
const DEMO_ACCOUNTS = [];

export default function Sidebar({ 
  title, 
  menuItems, 
  activeTab, 
  setActiveTab, 
  onTabChange, 
  user, 
  onLogout, 
  onSwitchAccount,
  onViewAs,
  isViewingAs,
  onReturnToMaster,
  masterSession,
  hideUserSection = false,
  groupedMenu = false  // New prop for grouped menu support
}) {
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Check if current session is Master Manager (either directly or has master session)
  const isMasterManager = user?.email === 'master@veriflow.com' || masterSession?.user?.email === 'master@veriflow.com';

  // Support both setActiveTab and onTabChange for backwards compatibility
  const handleTabChange = (tabId) => {
    if (setActiveTab) setActiveTab(tabId);
    if (onTabChange) onTabChange(tabId);
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Initialize expanded groups based on active tab
  const isGroupExpanded = (groupId, items) => {
    if (expandedGroups[groupId] !== undefined) return expandedGroups[groupId];
    // Auto-expand if active tab is in this group
    return items?.some(item => item.id === activeTab);
  };

  const handleSwitchAccount = async (account) => {
    setSwitching(true);
    try {
      const response = await axios.post('/auth/login', { 
        email: account.email, 
        password: account.password 
      });
      
      // If Master Manager is viewing and selecting another account, use View As
      if (isMasterManager && !account.isMaster && onViewAs) {
        toast.success(`Now viewing as ${account.role}`);
        setSwitchOpen(false);
        onViewAs(response.data.token, response.data.user);
        return;
      }
      
      // Regular switch (full login)
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      toast.success(`Switched to ${account.role}`);
      setSwitchOpen(false);
      
      // Callback to update user in App.js or reload
      if (onSwitchAccount) {
        onSwitchAccount(response.data.token, response.data.user);
      } else {
        window.location.reload();
      }
    } catch (error) {
      toast.error(`Failed to switch: ${error.response?.data?.detail || 'Login failed'}`);
    } finally {
      setSwitching(false);
    }
  };

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    
    // Handle link items that navigate to different pages
    if (item.isLink && item.href) {
      return (
        <a
          href={item.href}
          className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors text-slate-300 hover:bg-slate-800 hover:text-white`}
          data-testid={`menu-${item.id}`}
        >
          <Icon className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm">{item.label}</span>
          <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
        </a>
      );
    }

    // External link items
    if (item.isExternal && item.link) {
      return (
        <a
          href={item.link}
          className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors text-slate-300 hover:bg-slate-800 hover:text-white`}
          data-testid={`menu-${item.id}`}
        >
          <Icon className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm">{item.label}</span>
          <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
        </a>
      );
    }
    
    return (
      <button
        onClick={() => handleTabChange(item.id)}
        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors ${
          activeTab === item.id
            ? 'bg-emerald-600 text-white shadow-md'
            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
        }`}
        data-testid={`menu-${item.id}`}
      >
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        <span className="text-sm">{item.label}</span>
        {item.badge && (
          <span className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
            {item.badge}
          </span>
        )}
      </button>
    );
  };

  const renderGroupedMenu = () => {
    return menuItems.map((group) => {
      if (group.isGroup) {
        const GroupIcon = group.icon;
        const isExpanded = isGroupExpanded(group.id, group.items);
        const hasActiveChild = group.items?.some(item => item.id === activeTab);
        
        return (
          <li key={group.id} className="mb-1">
            <button
              onClick={() => toggleGroup(group.id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors ${
                hasActiveChild
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
              data-testid={`menu-group-${group.id}`}
            >
              <GroupIcon className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm font-medium">{group.label}</span>
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 ml-auto" />
              ) : (
                <ChevronRight className="w-4 h-4 ml-auto" />
              )}
            </button>
            {isExpanded && (
              <ul className="mt-1 ml-4 space-y-1 border-l border-slate-700 pl-2">
                {group.items.map((item) => (
                  <li key={item.id}>
                    {renderMenuItem(item)}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      }
      
      // Regular menu item (not in a group)
      return (
        <li key={group.id} className="mb-1">
          {renderMenuItem(group)}
        </li>
      );
    });
  };

  const renderFlatMenu = () => {
    return menuItems.map((item) => (
      <li key={item.id}>
        {renderMenuItem(item)}
      </li>
    ));
  };

  return (
    <aside className="w-64 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white flex flex-col border-r border-slate-800 relative">
      {/* Accent stripe (Veecheck brand colors) */}
      <div className="absolute top-0 left-0 right-0 h-1 flex">
        <div className="flex-1 bg-red-500"></div>
        <div className="flex-1 bg-yellow-400"></div>
        <div className="flex-1 bg-green-500"></div>
      </div>

      {/* Logo */}
      <div className="p-5 border-b border-slate-800 bg-slate-900/60">
        <div className="flex items-center justify-center bg-white rounded-lg p-2 shadow-lg">
          <img
            src={LOGO_URL}
            alt="Veecheck"
            className="h-12 w-auto object-contain"
          />
        </div>
        <p className="text-center text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-2">
          Background Verification
        </p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <ul className="space-y-1">
          {groupedMenu ? renderGroupedMenu() : renderFlatMenu()}
        </ul>
      </nav>

      {/* User Info & Actions - Hidden when hideUserSection is true */}
      {!hideUserSection && (
      <div className="p-4 border-t border-slate-700">
        {/* Notification Bell */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm text-slate-400">Signed in as</p>
            <p className="text-sm font-medium truncate">{user.name}</p>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
            {isMasterManager && !isViewingAs && (
              <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-500/20 text-indigo-300 text-xs rounded-full">
                Master Manager
              </span>
            )}
          </div>
          <div className="bg-slate-700 rounded-full">
            <NotificationCenter />
          </div>
        </div>
        
        {/* Return to Master Button (when viewing as another user) */}
        {isViewingAs && onReturnToMaster && (
          <Button
            onClick={onReturnToMaster}
            variant="ghost"
            className="w-full justify-start text-indigo-300 hover:text-white hover:bg-indigo-600 mb-2 border border-indigo-500/30"
            data-testid="sidebar-return-to-master"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return to Master
          </Button>
        )}
        
        {/* Switch Account / View As Button */}
        <Popover open={switchOpen} onOpenChange={setSwitchOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-between text-slate-300 hover:text-white hover:bg-slate-800 mb-2"
              data-testid="switch-account-button"
            >
              <span className="flex items-center">
                {isMasterManager ? <Eye className="w-4 h-4 mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                {isMasterManager ? 'View As User' : 'Switch Account'}
              </span>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="start" side="top">
            <div className="space-y-1">
              {isMasterManager && (
                <p className="text-xs font-medium text-indigo-600 px-2 py-1 bg-indigo-50 rounded mb-2">
                  👁️ Demo Mode - Select user to view
                </p>
              )}
              <p className="text-xs font-medium text-slate-500 px-2 py-1">
                {isMasterManager ? 'Available Users' : 'Quick Switch'}
              </p>
              {DEMO_ACCOUNTS.map((account) => (
                <button
                  key={account.email}
                  onClick={() => handleSwitchAccount(account)}
                  disabled={switching || user.email === account.email}
                  className={`w-full flex items-center gap-2 px-2 py-2 rounded text-sm text-left transition-colors ${
                    user.email === account.email 
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                      : 'hover:bg-slate-100 text-slate-700'
                  }`}
                  data-testid={`switch-to-${account.role.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className={`w-2 h-2 rounded-full ${account.color}`}></span>
                  <span className="flex-1">{account.role}</span>
                  {user.email === account.email && (
                    <span className="text-xs text-slate-400">(current)</span>
                  )}
                  {isMasterManager && !account.isMaster && user.email !== account.email && (
                    <Eye className="w-3 h-3 text-indigo-400" />
                  )}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Logout Button - Hidden when Master is viewing as another user */}
        {!isViewingAs && (
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        )}
      </div>
      )}
    </aside>
  );
}